import { Fragment } from 'react';
import {
  ChatCircleDots,
  FolderSimple,
  Sliders,
  Icon,
  Video,
} from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import { AddConversationButton } from 'src/v2/components/AddConversationButton';
import { Button } from 'src/v2/commonComponents/Button';
import {
  useAttachedImageTooltipActions,
  useAttachedImageTooltipState,
  useBreakpoint,
  useChatHistory,
  useConversationActions,
  useConversations,
  useLeftPanelActions,
  useLeftPanelState,
  useRightSidePanelActions,
  useRightSidePanelState,
} from 'src/hooks';
import classNames from 'classnames';
import styles from './NavSidebar.module.scss';
import { AppRoutes, LeftPanelSections } from 'src/types';
import { UserImage } from 'src/v2/components/UserImage';
import { DownloadAppsButton } from '../DownloadAppsButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { NewContentButton } from 'src/v2/components/NewContentButton';

interface ActionItem {
  section: LeftPanelSections;
  Icon: Icon;
  tooltipContent: string;
  visible: boolean;
  isBeta?: boolean;
  withBorder?: boolean;
  withRedDot?: boolean;
  disabled?: boolean;
}

export const NavSidebar = () => {
  const { isNinjaRetired } = useChatHistory();
  const { isDesktopAndUp } = useBreakpoint();
  const { toggleRightSidePanelExpanded } = useRightSidePanelActions();
  const { toggleLeftPanelExpanded, setLeftPanelData } = useLeftPanelActions();
  const { isLeftPanelExpanded, leftPanelSections } = useLeftPanelState();
  const { isImageEditSectionExpanded } = useRightSidePanelState();
  const { isAttachedImageEditingMode } = useAttachedImageTooltipState();
  const {
    onChangeIsAttachedImageEditingMode,
    onToggleImageEditTooltipVisible,
  } = useAttachedImageTooltipActions();
  const { hasUnreadMessage } = useConversations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setTaskSkillFilter } = useConversationActions();
  const isChatHistory = pathname === AppRoutes.CHAT_HISTORY;

  // const shouldShowReferrals = isSubscribedTier && !isEnterpriseTrialTier;

  const ACTION_LIST: ActionItem[] = [
    {
      section: LeftPanelSections.CONVERSATION_LIST,
      Icon: ChatCircleDots,
      tooltipContent: 'Chat history',
      visible: true,
      withRedDot: hasUnreadMessage,
    },
    {
      section: LeftPanelSections.FILE_LIST,
      Icon: FolderSimple,
      tooltipContent: 'Files',
      visible: isDesktopAndUp,
      isBeta: true,
      withBorder: true,
    },
    {
      section: LeftPanelSections.SETTINGS,
      Icon: Sliders,
      tooltipContent: 'Settings',
      visible: !(isNinjaRetired && isChatHistory),
      withBorder: true,
    },
    {
      section: LeftPanelSections.VIDEO_TUTORIALS,
      Icon: Video,
      tooltipContent: 'Video tutorials',
      visible: false, // TODO: remove if not needed in the future
      withRedDot: true,
    },
  ];

  const handleButtonClick = (panelType: LeftPanelSections) => {
    if (leftPanelSections === panelType && isLeftPanelExpanded) {
      toggleLeftPanelExpanded(false);
      return;
    }

    setLeftPanelData({
      panelType,
      isExpanded:
        panelType !== LeftPanelSections.VIDEO_TUTORIALS &&
        panelType !== LeftPanelSections.API_MANAGEMENT,
    });

    if (isImageEditSectionExpanded) {
      toggleRightSidePanelExpanded(false);
    }

    if (isAttachedImageEditingMode) {
      onChangeIsAttachedImageEditingMode(false);
      onToggleImageEditTooltipVisible(true);
    }
  };

  const handleActionClick = (panelType: LeftPanelSections) => {
    setTaskSkillFilter();
    handleButtonClick(panelType);

    if (
      pathname !== AppRoutes.HOME &&
      panelType !== LeftPanelSections.API_MANAGEMENT &&
      panelType !== LeftPanelSections.VIDEO_TUTORIALS &&
      !isNinjaRetired
    ) {
      navigate(AppRoutes.HOME);
    }

    if (panelType === LeftPanelSections.API_MANAGEMENT) {
      navigate(`${AppRoutes.ADD_ON}`);
    }

    if (panelType === LeftPanelSections.VIDEO_TUTORIALS) {
      navigate(AppRoutes.VIDEO_TUTORIALS);
    }
  };

  return (
    <div
      className={classNames(styles.root, {
        [styles.withBorder]: isLeftPanelExpanded,
        [styles.withShadow]: !isLeftPanelExpanded,
      })}
    >
      <div className={styles.container}>
        <AddConversationButton appearance="ninja-icon" />

        {isDesktopAndUp && !isNinjaRetired && <NewContentButton />}

        {ACTION_LIST.map(
          ({
            section,
            Icon,
            tooltipContent,
            visible,
            isBeta,
            withBorder,
            withRedDot,
            disabled,
          }) => (
            <Fragment key={section}>
              {visible && (
                <Button
                  color="transparent"
                  shape="round"
                  onClick={() => handleActionClick(section)}
                  tooltipContent={tooltipContent}
                  className={classNames(styles.button, {
                    [styles.active]:
                      isLeftPanelExpanded && leftPanelSections === section,
                    [styles.withRedDot]: withRedDot,
                    [styles.withLabel]: isBeta,
                  })}
                  data-e2e={`action-${section}-button`}
                  disabled={disabled}
                >
                  <Icon size={SVG_SIZE_M} />
                </Button>
              )}

              {withBorder && !isNinjaRetired && <hr className="divider" />}
            </Fragment>
          ),
        )}
      </div>

      <div className={styles.container}>
        {/* TODO: hide referrals for now */}
        {/* {shouldShowReferrals && (
          <Button
            color="transparent"
            shape="round"
            onClick={() => handleActionClick(LeftPanelSections.REFERRALS)}
            tooltipContent={
              !shouldShowFreeMonths ? 'Referral' : 'Referral & reward'
            }
            className={classNames(styles.button, styles.bottomButton, {
              [styles.active]:
                isLeftPanelExpanded &&
                leftPanelSections === LeftPanelSections.REFERRALS,
            })}
            data-e2e="action-referrals-button"
          >
            <Gift size={SVG_SIZE_M} />
          </Button>
        )} */}

        {!(isNinjaRetired && isChatHistory) && <DownloadAppsButton />}

        {!(isNinjaRetired && isChatHistory) && (
          <Button
            data-e2e="user-menu-button"
            color="transparent"
            shape="round"
            onClick={() => handleButtonClick(LeftPanelSections.USER_MENU)}
            tooltipContent="Manage account & settings"
          >
            <UserImage />
          </Button>
        )}
      </div>
    </div>
  );
};
